// Module `staff`: pages for Convenor staff (PLATFORM_ADMINS), across every
// organisation. The email log lists what the platform sent, never the message
// text: it holds sign-in links and codes.
import { z } from "zod";
import { ORG_TIERS } from "../enums";
import { LIMITS } from "../limits";
import type { TrialPhase } from "../trial";
import { email, type Id, text } from "./common";

export const EMAIL_LOG_STATUSES = ["queued", "retrying", "sent", "failed"] as const;
export type EmailLogStatus = (typeof EMAIL_LOG_STATUSES)[number];

export const emailLogQuery = z.object({
  status: z.enum(EMAIL_LOG_STATUSES).optional(),
  /** Part of the recipient's address. */
  q: z.string().trim().max(254).optional(),
  /** Cursor: the `next` of the previous page ("<ISO time>_<id>"). */
  before: z
    .string()
    .regex(/^[^_]+_[0-9a-f-]{36}$/)
    .refine((s) => !Number.isNaN(Date.parse(s.split("_")[0]!)))
    .optional(),
});
export type EmailLogQuery = z.infer<typeof emailLogQuery>;

export type EmailLogEntry = {
  id: Id;
  queuedAt: string;
  orgName: string | null;
  to: string;
  subject: string;
  status: EmailLogStatus;
  attempts: number;
  /** Sent, or given up on. */
  finishedAt: string | null;
  /** When the next try is due (queued or retrying). */
  nextTryAt: string | null;
  /** The last error's first line. */
  error: string | null;
  attachments: number;
};

export type EmailLogPage = { entries: EmailLogEntry[]; next: string | null };

/** One organisation on the trials page: a trial, or an organisation that was a trial and has paid. */
export type StaffTrial = {
  orgId: Id;
  name: string;
  plan: "trial" | "paid";
  createdAt: string;
  /** When the trial ends (null once paid). */
  trialEndsAt: string | null;
  /** True once staff used the one 14-day extension. */
  extended: boolean;
  phase: TrialPhase;
  daysLeft: number | null;
  /** True when the organisation may be removed now (a cancelled or unpaid trial after day 60, or a first year never paid). */
  removable?: boolean;
  /** True when the first year was charged but the charge failed and was never paid (staff can mark it paid, e.g. after a bank transfer). */
  firstYearUnpaid?: boolean;
  /** The longest-standing owner. */
  ownerEmail: string | null;
};
export type StaffTrialsResponse = { trials: StaffTrial[] };

/** "Set up a customer" (staff): the organisation, and the owner who is invited to it. Customers set up here pay by invoice. */
export const STAFF_ORG_PAYMENTS = ["trial", "paid"] as const;
export type StaffOrgPayment = (typeof STAFF_ORG_PAYMENTS)[number];
export const staffCreateOrgBody = z.object({
  name: text(LIMITS.orgName, "Organisation name"),
  ownerEmail: email,
  ownerName: text(LIMITS.userName, "Owner's name"),
  payment: z.enum(STAFF_ORG_PAYMENTS),
  tier: z.enum(ORG_TIERS),
});
export type StaffCreateOrgBody = z.infer<typeof staffCreateOrgBody>;
export type StaffCreateOrgResponse = { orgId: Id; invitationSentTo: string };

/** "Remove now" (staff): the organisation's name, typed again to confirm. */
export const staffRemoveOrgBody = z.object({ confirmName: z.string().max(200) });
export type StaffRemoveOrgBody = z.infer<typeof staffRemoveOrgBody>;
